import {vec3, vec4, mat4, mat3} from 'gl-matrix';
import Drawable from './Drawable';
import {gl} from '../../globals';

var activeProgram: WebGLProgram = null;

export class Shader {
  shader: WebGLShader;

  constructor(type: number, source: string) {
    this.shader = gl.createShader(type);
    gl.shaderSource(this.shader, source);
    gl.compileShader(this.shader);

    if (!gl.getShaderParameter(this.shader, gl.COMPILE_STATUS)) {
      throw gl.getShaderInfoLog(this.shader);
    }
  }
};

class ShaderProgram {
  prog: WebGLProgram;

  attrPos: number;
  attrNor: number;
  attrCol: number; // This time, it's an instanced rendering attribute, so each particle can have a unique color. Not per-vertex, but per-instance.
  attrUV: number;

  attrMatrixC0: number; //added for instanced matrices 
  attrMatrixC1: number; //added for instanced matrices 
  attrMatrixC2: number; //added for instanced matrices 
  attrMatrixC3: number; //added for instanced matrices
  
  unifSkyType: WebGLUniformLocation; //added for sky type 

  unifModel: WebGLUniformLocation;
  unifModelInvTr: WebGLUniformLocation;
  unifViewProj: WebGLUniformLocation;
  unifCameraAxes: WebGLUniformLocation;
  unifTime: WebGLUniformLocation;
  unifRef: WebGLUniformLocation;
  unifEye: WebGLUniformLocation;
  unifUp: WebGLUniformLocation;
  unifDimensions: WebGLUniformLocation;

  constructor(shaders: Array<Shader>) {
    this.prog = gl.createProgram();

    for (let shader of shaders) {
      gl.attachShader(this.prog, shader.shader);
    }
    gl.linkProgram(this.prog);
    if (!gl.getProgramParameter(this.prog, gl.LINK_STATUS)) {
      throw gl.getProgramInfoLog(this.prog);
    }

    this.attrPos = gl.getAttribLocation(this.prog, "vs_Pos");
    this.attrCol = gl.getAttribLocation(this.prog, "vs_Col");
    this.attrNor = gl.getAttribLocation(this.prog, "vs_Nor");
    this.attrUV = gl.getAttribLocation(this.prog, "vs_UV");
    this.unifDimensions = gl.getUniformLocation(this.prog, "u_Dimensions");
    this.unifModel      = gl.getUniformLocation(this.prog, "u_Model");
    this.unifModelInvTr = gl.getUniformLocation(this.prog, "u_ModelInvTr");
    this.unifViewProj   = gl.getUniformLocation(this.prog, "u_ViewProj");
    this.unifCameraAxes      = gl.getUniformLocation(this.prog, "u_CameraAxes");
    this.unifTime      = gl.getUniformLocation(this.prog, "u_Time");
    this.unifEye   = gl.getUniformLocation(this.prog, "u_Eye");
    this.unifRef   = gl.getUniformLocation(this.prog, "u_Ref");
    this.unifUp   = gl.getUniformLocation(this.prog, "u_Up");
    this.unifSkyType = gl.getUniformLocation(this.prog, "u_SkyType"); //added this for sky type

    this.attrMatrixC0 = gl.getAttribLocation(this.prog, "vs_Matrix_col0"); //added for instanced matrix transformations
    this.attrMatrixC1 = gl.getAttribLocation(this.prog, "vs_Matrix_col1");
    this.attrMatrixC2 = gl.getAttribLocation(this.prog, "vs_Matrix_col2");
    this.attrMatrixC3 = gl.getAttribLocation(this.prog, "vs_Matrix_col3");
  }

  use() {
    if (activeProgram !== this.prog) {
      gl.useProgram(this.prog);
      activeProgram = this.prog;
    }
  }

  setEyeRefUp(eye: vec3, ref: vec3, up: vec3) {
    this.use();
    if(this.unifEye !== -1) {
      gl.uniform3f(this.unifEye, eye[0], eye[1], eye[2]);
    }
    if(this.unifRef !== -1) {
      gl.uniform3f(this.unifRef, ref[0], ref[1], ref[2]);
    }
    if(this.unifUp !== -1) {
      gl.uniform3f(this.unifUp, up[0], up[1], up[2]);
    }
  }

  setDimensions(width: number, height: number) {
    this.use();
    if(this.unifDimensions !== -1) {
      gl.uniform2f(this.unifDimensions, width, height);
    }
  }

  setModelMatrix(model: mat4) {
    this.use();
    if (this.unifModel !== -1) {
      gl.uniformMatrix4fv(this.unifModel, false, model);
    }

    if (this.unifModelInvTr !== -1) {
      let modelinvtr: mat4 = mat4.create();
      mat4.transpose(modelinvtr, model);
      mat4.invert(modelinvtr, modelinvtr);
      gl.uniformMatrix4fv(this.unifModelInvTr, false, modelinvtr);
    }
  }

  setViewProjMatrix(vp: mat4) {
    this.use();
    if (this.unifViewProj !== -1) {
      gl.uniformMatrix4fv(this.unifViewProj, false, vp);
    }
  }

  setCameraAxes(axes: mat3) {
    this.use();
    if (this.unifCameraAxes !== -1) {
      gl.uniformMatrix3fv(this.unifCameraAxes, false, axes);
    }
  }

  setTime(t: number) {
    this.use();
    if (this.unifTime !== -1) {
      gl.uniform1f(this.unifTime, t);
    }
  }

  //added this
  setSkyType(num: number) {
    this.use();
    if(this.unifSkyType !== -1) {
      gl.uniform1f(this.unifSkyType, num);
    }
  }

  draw(d: Drawable) {
    this.use();

    if (this.attrPos != -1 && d.bindPos()) {
      gl.enableVertexAttribArray(this.attrPos);
      gl.vertexAttribPointer(this.attrPos, 4, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(this.attrPos, 0); // Advance 1 index in pos VBO for each vertex
    }

    if (this.attrNor != -1 && d.bindNor()) {
      gl.enableVertexAttribArray(this.attrNor);
      gl.vertexAttribPointer(this.attrNor, 4, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(this.attrNor, 0); // Advance 1 index in nor VBO for each vertex
    }

    if (this.attrCol != -1 && d.bindCol()) {
      gl.enableVertexAttribArray(this.attrCol);
      gl.vertexAttribPointer(this.attrCol, 4, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(this.attrCol, 1); // Advance 1 index in col VBO for each drawn instance
    }

    if (d.bindTranslate()) {
      //added bindMatrices instead of translate
    }

    if (this.attrUV != -1 && d.bindUV()) {
      gl.enableVertexAttribArray(this.attrUV);
      gl.vertexAttribPointer(this.attrUV, 2, gl.FLOAT, false, 0, 0);
      gl.vertexAttribDivisor(this.attrUV, 0); // Advance 1 index in pos VBO for each vertex
    }

    //added
    if (d.bindMatrices()) {
      //console.log("get here"); 
      if (this.attrMatrixC0 != -1) {
        gl.enableVertexAttribArray(this.attrMatrixC0); //          stride, offset
        gl.vertexAttribPointer(this.attrMatrixC0, 4, gl.FLOAT, false, 64, 0);
        gl.vertexAttribDivisor(this.attrMatrixC0, 1); // Advance 1 index in translate VBO for each drawn instance
      }
      if (this.attrMatrixC1 != -1) {
        gl.enableVertexAttribArray(this.attrMatrixC1);
        gl.vertexAttribPointer(this.attrMatrixC1, 4, gl.FLOAT, false, 64, 16);
        gl.vertexAttribDivisor(this.attrMatrixC1, 1); // Advance 1 index in translate VBO for each drawn instance
      }
      if (this.attrMatrixC2 != -1) {
        gl.enableVertexAttribArray(this.attrMatrixC2);
        gl.vertexAttribPointer(this.attrMatrixC2, 4, gl.FLOAT, false, 64, 32);
        gl.vertexAttribDivisor(this.attrMatrixC2, 1); // Advance 1 index in translate VBO for each drawn instance
      }
      if (this.attrMatrixC3 != -1) {
        gl.enableVertexAttribArray(this.attrMatrixC3);
        gl.vertexAttribPointer(this.attrMatrixC3, 4, gl.FLOAT, false, 64, 48);
        gl.vertexAttribDivisor(this.attrMatrixC3, 1); // Advance 1 index in translate VBO for each drawn instance
      }
    }

    // TODO: Set up attribute data for additional instanced rendering data as needed

    d.bindIdx();
    // drawElementsInstanced uses the vertexAttribDivisor for each "in" variable to
    // determine how to link it to each drawn instance of the bound VBO.
    // For example, the index used to look in the VBO associated with
    // vs_Pos (attrPos) is advanced by 1 for each thread of the GPU running the
    // vertex shader since its divisor is 0.
    // On the other hand, the index used to look in the VBO associated with
    // vs_Translate (attrTranslate) is advanced by 1 only when the next instance
    // of our drawn object (in the base code example, the square) is processed
    // by the GPU, thus being the same value for the first set of four vertices,
    // then advancing to a new value for the next four, then the next four, and
    // so on.
    gl.drawElementsInstanced(d.drawMode(), d.elemCount(), gl.UNSIGNED_INT, 0, d.numInstances);

    if (this.attrPos != -1) gl.disableVertexAttribArray(this.attrPos);
    if (this.attrNor != -1) gl.disableVertexAttribArray(this.attrNor);
    if (this.attrCol != -1) gl.disableVertexAttribArray(this.attrCol);
    if (this.attrUV != -1) gl.disableVertexAttribArray(this.attrUV);
    if (this.attrMatrixC0 != -1) gl.disableVertexAttribArray(this.attrMatrixC0); //added
    if (this.attrMatrixC1 != -1) gl.disableVertexAttribArray(this.attrMatrixC1); //added
    if (this.attrMatrixC2 != -1) gl.disableVertexAttribArray(this.attrMatrixC2); //added
    if (this.attrMatrixC3 != -1) gl.disableVertexAttribArray(this.attrMatrixC3); //added
  }
};

export default ShaderProgram;



// WEBPACK FOOTER //
// ./src/rendering/gl/ShaderProgram.ts